import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Layers, Eye } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useVendorFormations, useVendorRefresh } from "@/hooks/useVendor";
import { useFormationCategories } from "@/hooks/useFormations";
import { formatPrice } from "@/lib/format";
import { Pill } from "@/pages/admin/ui";
import { FormationFormModal, ProgramModal, VENDOR_FORMATION_ENDPOINTS } from "@/components/formations/FormationEditor";

export default function VendorFormations() {
  const { data: formations, isLoading } = useVendorFormations();
  const { data: catData } = useFormationCategories();
  const categories = Array.isArray(catData) ? catData : (catData?.categories || []);
  const refresh = useVendorRefresh();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [programFor, setProgramFor] = useState(null);

  const reload = () => refresh(["vendor-formations", "vendor-dashboard"]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (f) => { setEditing(f); setFormOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette formation, ses modules et ses cours ?")) return;
    try { await api.delete(`/vendor/formations/${id}`); toast.success("Formation supprimée"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (f, status) => {
    try { await api.put(`/vendor/formations/${f.id}/status`, { status }); toast.success("Statut mis à jour"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-formations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Mes formations</h1>
          <p className="mt-1 text-sm text-slate-500">Créez des formations complètes : images, vidéos, modules, cours, fichiers et liens.</p>
        </div>
        <button onClick={openNew} data-testid="vendor-add-formation" className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
          <Plus className="h-4 w-4" /> Ajouter une formation
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !formations?.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <p className="text-sm text-slate-500">Aucune formation. Créez votre première formation.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {formations.map((f) => (
            <div key={f.id} data-testid={`vformation-${f.id}`} className="overflow-hidden rounded-2xl border border-line bg-white">
              <div className="relative h-32 bg-slate-100">
                {f.image_url ? <img src={fileUrl(f.image_url)} alt="" className="h-full w-full object-cover" /> : null}
                <div className="absolute left-3 top-3">
                  <Pill tone={f.status === "published" ? "green" : f.status === "unpublished" ? "rose" : "slate"}>
                    {f.status === "published" ? "Publiée" : f.status === "unpublished" ? "Dépubliée" : "Brouillon"}
                  </Pill>
                </div>
              </div>
              <div className="p-4">
                <p className="line-clamp-1 font-display text-base font-bold text-ink">{f.title}</p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{f.short_description}</p>
                <p className="mt-2 font-bold text-brand">{f.price ? formatPrice(f.price) : "Gratuit"}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button onClick={() => setProgramFor(f)} data-testid={`vformation-program-${f.id}`}
                    className="flex items-center gap-1 rounded-lg bg-ink px-2.5 py-1.5 text-[11px] font-bold text-white">
                    <Layers className="h-3 w-3" /> Programme
                  </button>
                  <button onClick={() => openEdit(f)} data-testid={`vformation-edit-${f.id}`}
                    className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-bold text-slate-600 hover:border-brand hover:text-brand">
                    <Pencil className="h-3 w-3" /> Modifier
                  </button>
                  {f.status === "published" ? (
                    <button onClick={() => setStatus(f, "unpublished")} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-bold text-slate-600">
                      <Eye className="h-3 w-3" /> Dépublier
                    </button>
                  ) : (
                    <button onClick={() => setStatus(f, "published")} data-testid={`vformation-publish-${f.id}`}
                      className="flex items-center gap-1 rounded-lg border border-emerald-200 px-2.5 py-1.5 text-[11px] font-bold text-emerald-600">
                      Publier
                    </button>
                  )}
                  <button onClick={() => remove(f.id)} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1.5 text-[11px] font-bold text-rose-500 hover:bg-rose-50">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FormationFormModal open={formOpen} onClose={() => setFormOpen(false)} editing={editing}
        categories={categories} onSaved={reload} endpoints={VENDOR_FORMATION_ENDPOINTS} />
      {programFor && (
        <ProgramModal open onClose={() => setProgramFor(null)} formation={programFor} endpoints={VENDOR_FORMATION_ENDPOINTS} />
      )}
    </div>
  );
}
